
import React from 'react';
import ResultCard from '../search-form/results/ResultCard';
import FlightMultiResultCard from '../search-form/results/FlightMultiResultCard';
import FlightTrainResultCard from '../search-form/results/FlightTrainResultCard';

const formatPrice = (amount) => {
    const val = parseFloat(typeof amount === 'string' ? amount.replace(/,/g, '') : amount);
    if (isNaN(val) || !amount) return null;
    return val.toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    });
};

const TravelResultsList = ({ routes, onBookClick, onShowError }) => {
    if (!routes || routes.length === 0) return null;

    const mapRouteToCardProps = (route) => {
        const nodes = route.nodes || [];
        const isMultiSegment = nodes.length > 1;

        // Determine Route Type
        let hasFlight = false;
        let hasTrain = false;
        let hasBus = false;
        const routeType = route.route_type;

        if (routeType === 1) {
            hasTrain = true;
        } else if (routeType === 2) {
            hasFlight = true;
        } else if (routeType === 3) {
            hasBus = true;
        } else if (routeType === 4 || routeType === 5) { // 5 is sometimes used for mixed too in some APIs
            hasTrain = true;
            hasFlight = true;
        } else {
            nodes.forEach(n => {
                const seg = n.segment || {};
                if (seg.flight_num || (seg.carrier && !seg.train_name)) hasFlight = true;
                if (seg.train_name || seg.train_num) hasTrain = true;
                if (seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num)) hasBus = true;
            });
        }

        const getRootTotalFare = (r) => {
            if (r.total_fare !== null && r.total_fare !== undefined) return r.total_fare;
            let sum = 0;
            let foundAny = false;
            r.nodes?.forEach(n => {
                const f = n.segment?.seat_avail_ui?.fare || n.segment?.fare;
                if (f !== null && f !== undefined) {
                    sum += parseFloat(f);
                    foundAny = true;
                }
            });
            return foundAny ? sum : null;
        };

        const calculatedTotalFare = getRootTotalFare(route);

        // Single Segment Card (Train, Flight or Bus)
        if (!isMultiSegment) {
            const segment = nodes[0]?.segment;
            let type = 'train';
            if (hasFlight) type = 'flight';
            else if (hasBus) type = 'bus';

            return {
                Component: ResultCard,
                props: {
                    type: type,
                    tags: route.tags || [],
                    segment: {
                        ...segment,
                        mode: type,
                        // Pass raw fields for ResultCard to format
                        origin_name: segment?.origin_name,
                        origin_code: segment?.origin_code_iata || segment?.origin_code || (type === 'bus' && segment?.origin_name ? segment.origin_name.substring(0, 3).toUpperCase() : ''),
                        origin_dep_dt: segment?.origin_dep_dt,
                        dest_name: segment?.dest_name,
                        dest_code: segment?.dest_code_iata || segment?.dest_code || (type === 'bus' && segment?.dest_name ? segment.dest_name.substring(0, 3).toUpperCase() : ''),
                        dest_arr_dt: segment?.dest_arr_dt,
                        flight_num: segment?.flight_num || segment?.train_num || segment?.service_name || '',
                        flight_name: segment?.carrier || segment?.train_name || segment?.travel_name || '',
                        // Pass seat info from segment to top level for easy access in generic card
                        seat_class_ui: segment?.seat_class_ui || (type === 'bus' ? (segment?.seat_avail_ui?.class || segment?.bus_type) : ''),
                        seat_avail_ui: segment?.seat_avail_ui,
                        category: segment?.category || (type === 'bus' ? 'Bus' : ''),
                        booking_link: segment?.booking_link
                    },
                    price: formatPrice(calculatedTotalFare),
                    duration: segment?.duration_mins ? `${Math.floor(segment.duration_mins / 60)}h ${segment.duration_mins % 60}m` : 'N/A',
                    stops: 0,
                    seatStatus: (segment?.seat_avail_ui && Object.keys(segment.seat_avail_ui).length > 0)
                        ? (segment.seat_avail_ui.is_avail ? "Seats available" : "Seats not available")
                        : null,
                    originCommentWeb: segment?.origin_comment_web || null,
                    originCommentWebTitle: segment?.origin_comment_web_title || null,
                    destCommentWeb: segment?.dest_comment_web || null,
                    destCommentWebTitle: segment?.dest_comment_web_title || null,
                    explanation: route.explanation || null
                }
            };
        }

        // Multi Segment Logic
        const legs = [];
        nodes.forEach((node, index) => {
            const seg = node.segment || {};
            const isFlight = !!(seg.flight_num || (seg.carrier && !seg.train_name));
            const isTrain = !!(seg.train_name || seg.train_num);
            const isBus = !!(seg.bus_type || (seg.travel_name && !seg.train_name && !seg.flight_num));

            // Segment Leg
            legs.push({
                type: isFlight ? 'flight' : isTrain ? 'train' : isBus ? 'bus' : 'layover',
                number: seg.flight_num || seg.train_num || seg.service_name,
                name: seg.carrier || seg.train_name || seg.travel_name,
                origin: {
                    name: seg.origin_name,
                    code: seg.origin_code_iata || seg.origin_code,
                    time: seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                    date: seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '',
                    dep_dt: seg.origin_dep_dt // Raw timestamp for refresh
                },
                destination: {
                    name: seg.dest_name,
                    code: seg.dest_code_iata || seg.dest_code,
                    time: seg.dest_arr_dt ? new Date(seg.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                    date: seg.dest_arr_dt ? new Date(seg.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
                },
                duration: `${Math.floor(seg.duration_mins / 60)}h ${seg.duration_mins % 60}m`,
                fare: seg.seat_avail_ui?.fare || seg.fare || null,
                price: formatPrice(seg.seat_avail_ui?.fare || seg.fare),
                description: seg.origin_comment || seg.dest_comment || "Layover",
                avail: seg.seat_avail_ui?.avl || "Avail",
                class: seg.seat_class_ui || "E",
                halts: seg.stop_count || 0,
                footerText: seg.seat_avail_ui?.explanation || null,
                seat_avail_ui: seg.seat_avail_ui, // Full object for fallback
                seat_class_ui: seg.seat_class_ui, // Current class ID
                seat_classes: seg.seat_classes || [],
                seat_avail: seg.seat_avail || {},
                seat_costs: seg.seat_costs || {},
                category: seg.category || '',
                // Commute info
                originCommentWeb: seg.origin_comment_web || null,
                originCommentWebTitle: seg.origin_comment_web_title || null,
                destCommentWeb: seg.dest_comment_web || null,
                destCommentWebTitle: seg.dest_comment_web_title || null,
                booking_link: seg.booking_link,
                trace_id: seg.trace_id,
                result_idx: seg.result_idx,
                is_lcc: seg.is_lcc,
                valid_till_ts: seg.valid_till_ts,
                baggage: seg.baggage
            });


            // Intermediate Layover from stops_data if present
            if (index < nodes.length - 1 && route.stops_data && route.stops_data[index]) {
                const stop = route.stops_data[index];

                // Show only commute_comment if available to keep it clean. 
                // Fallback to timing explanation ONLY if no commute info exists.
                const explanation = stop.commute_comment || stop.exp_time_avl_explanation || stop.exp_time_avl_comment;

                if (explanation) {
                    legs.push({
                        type: 'layover',
                        title: stop.stop_title || "Layover",
                        location: stop.arr_at_name || stop.dep_from_name || "Connection",
                        description: explanation
                    });
                }
            }
        });

        const firstSeg = nodes[0].segment;
        const lastSeg = nodes[nodes.length - 1].segment;
        const totalStops = nodes.length - 1;

        const commonProps = {
            legs: legs,
            tags: route.tags || [],
            destCommentWeb: lastSeg.dest_comment_web || null,
            destCommentWebTitle: lastSeg.dest_comment_web_title || null,
            totalDuration: (() => {
                let totalMins = 0;
                let hasValidData = true;

                nodes.forEach((node, index) => {
                    const seg = node.segment || {};
                    const dur = parseInt(seg.duration_mins);
                    if (!isNaN(dur)) {
                        totalMins += dur;
                    } else {
                        hasValidData = false;
                    }

                    // Add layover duration if not the last segment
                    if (index < nodes.length - 1) {
                        const nextSeg = nodes[index + 1].segment || {};
                        const arrivalTime = new Date(seg.dest_arr_dt);
                        const departureTime = new Date(nextSeg.origin_dep_dt);

                        if (!isNaN(arrivalTime.getTime()) && !isNaN(departureTime.getTime())) {
                            const diffMins = Math.floor((departureTime - arrivalTime) / (1000 * 60));
                            // Only add if it's a positive layover (safety check)
                            if (diffMins > 0) {
                                totalMins += diffMins;
                            }
                        } else {
                            hasValidData = false;
                        }
                    }
                });

                if (hasValidData && totalMins > 0) {
                    return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`;
                }

                // Fallback: Original clock subtraction if segment data is missing
                const diffTime = new Date(lastSeg.dest_arr_dt) - new Date(firstSeg.origin_dep_dt);
                return `${Math.floor(diffTime / (1000 * 60 * 60))}h ${Math.floor((diffTime / (1000 * 60)) % 60)}m`;
            })(),
            price: formatPrice(calculatedTotalFare),
            origin: {
                name: firstSeg.origin_name,
                code: firstSeg.origin_code_iata || firstSeg.origin_code,
                time: new Date(firstSeg.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                date: new Date(firstSeg.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            },
            destination: {
                name: lastSeg.dest_name,
                code: lastSeg.dest_code_iata || lastSeg.dest_code,
                time: new Date(lastSeg.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                date: new Date(lastSeg.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
            },
            fareOptions: [{ class: "Economy", avail: "Available", price: formatPrice(calculatedTotalFare) }],
            stops: totalStops,
            halts: totalStops, // For mixed/train cards
            explanation: route.explanation || null,
            seatStatus: (() => {
                const hasAnyData = nodes.some(n => n.segment?.seat_avail_ui && Object.keys(n.segment.seat_avail_ui).length > 0);
                if (!hasAnyData) return null;

                const availList = nodes.map(n => !!n.segment?.seat_avail_ui?.is_avail);
                const allAvail = availList.every(v => v === true);
                const someAvail = availList.some(v => v === true);

                if (allAvail) return "Seats available";
                if (someAvail) return "Seats partially available";
                return "Seats not available";
            })()
        };

        if (hasTrain && hasFlight) {
            return {
                Component: FlightTrainResultCard,
                props: {
                    ...commonProps,
                    routeInfo: "Multiple Mode Route"
                }
            };
        } else {
            // Determine secondary mode info 
            let mode = 'flight';
            if (hasTrain) mode = 'train';
            else if (hasBus) mode = 'bus';

            return {
                Component: FlightMultiResultCard,
                props: {
                    ...commonProps,
                    mode: mode,
                    airlineInfo: nodes.map(n => n.segment?.carrier || n.segment?.train_name || n.segment?.travel_name).filter(Boolean).join(' + ')
                }
            };
        }
    };

    return (
        <div className="space-y-6">
            {routes.map((route, index) => {
                const { Component, props } = mapRouteToCardProps(route);
                return (
                    <div key={index} className="fade-in">
                        <Component card={props} onBookClick={onBookClick} onShowError={onShowError} />
                    </div>
                );
            })}
        </div>
    );
};

export default TravelResultsList;
